import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Scales, Lightning, WarningDiamond } from "@phosphor-icons/react";
import { toast } from "sonner";

const PREC_TONE = { gesetzlich: "red", hersteller: "amber", vertrag: "blue", default: "gray" };
const SEV_TONE = { kritisch: "red", hoch: "amber", mittel: "blue", niedrig: "gray" };

export default function Rules() {
  const [rules, setRules] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", type: "interval", scope: "*", precedence: "default", months: 12 });
  const [ev, setEv] = useState({ type: "interval", scope: "Sektionaltor" });
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/rules").then((r) => setRules(r.data));
    api.get("/catalogs").then((r) => setCatalogs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const config = form.type === "interval" ? { months: Number(form.months) || 12 } : {};
      await api.post("/rules", { key: form.key, name: form.name, type: form.type, scope: form.scope, precedence: form.precedence, config });
      toast.success("Regel veröffentlicht"); setOpen(false);
      setForm({ key: "", name: "", type: "interval", scope: "*", precedence: "default", months: 12 }); load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  const evaluate = async () => {
    try { const { data } = await api.post("/rules/evaluate", { type: ev.type, scope: ev.scope, context: {} }); setResult(data); }
    catch (e) { setResult(null); toast.error("Keine Regel", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!rules) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  const defects = catalogs.flatMap((c) => c.config?.defects || []);

  return (
    <div>
      <PageHeader title="Regelwerk / Rules Engine" subtitle="Regeln · Schwellwerte · Mängelkatalog · Decision-Service (versioniert)">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-rule-button" className="gap-2"><Plus size={18} weight="bold" />Neue Regel</Button></DialogTrigger>
          <DialogContent data-testid="rule-dialog"><DialogHeader><DialogTitle className="font-heading">Regel veröffentlichen</DialogTitle></DialogHeader>
            <DialogDescription>Versionierte Regel mit Präzedenz.</DialogDescription>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label2>Key *</Label2><Input data-testid="rule-key" value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} placeholder="interval.xyz" /></div>
                <div className="space-y-1.5"><Label2>Scope</Label2><Input value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} placeholder="* oder Anlagentyp" /></div>
              </div>
              <div className="space-y-1.5"><Label2>Name *</Label2><Input data-testid="rule-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label2>Typ</Label2>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="rule-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{["interval", "threshold", "decision", "catalog"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label2>Präzedenz</Label2>
                  <Select value={form.precedence} onValueChange={(v) => setForm((f) => ({ ...f, precedence: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["gesetzlich", "hersteller", "vertrag", "default"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              {form.type === "interval" && <div className="space-y-1.5"><Label2>Intervall (Monate)</Label2><Input type="number" value={form.months} onChange={(e) => setForm((f) => ({ ...f, months: e.target.value }))} /></div>}
            </div>
            <DialogFooter><Button data-testid="rule-save" onClick={submit} disabled={!form.key || !form.name}>Veröffentlichen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="mb-6 rounded-md border p-5" data-testid="decision-service">
        <div className="mb-3 flex items-center gap-2"><Lightning size={18} weight="duotone" className="text-primary" /><Label2>Decision-Service · Präzedenz gesetzlich &gt; hersteller &gt; vertrag &gt; default</Label2></div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5"><Label2>Typ</Label2>
            <Select value={ev.type} onValueChange={(v) => setEv((s) => ({ ...s, type: v }))}>
              <SelectTrigger className="w-40" data-testid="eval-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["interval", "threshold"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label2>Scope</Label2><Input className="w-44" data-testid="eval-scope" value={ev.scope} onChange={(e) => setEv((s) => ({ ...s, scope: e.target.value }))} /></div>
          <Button data-testid="eval-button" onClick={evaluate}>Auswerten</Button>
          {result && <div data-testid="eval-result" className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm">
            <span className="font-mono">{JSON.stringify(result.decision)}</span>
            <StatusBadge tone={PREC_TONE[result.precedence]}>{result.precedence}</StatusBadge>
            <span className="text-xs text-muted-foreground">{result.applied_rule} v{result.version}</span>
          </div>}
        </div>
      </Card>

      <h2 className="mb-3 font-heading text-xl font-bold tracking-tight">Regeln</h2>
      <Card className="mb-6 divide-y divide-border rounded-md border">
        {rules.filter((r) => r.type !== "catalog").map((r) => (
          <div key={r.id} data-testid="rule-row" className="flex items-center justify-between px-5 py-3.5">
            <div className="flex items-center gap-3">
              <Scales size={18} weight="duotone" className="text-primary" />
              <div><div className="font-semibold">{r.name} <span className="text-xs text-muted-foreground">v{r.version}</span></div>
                <div className="font-mono text-xs text-muted-foreground">{r.key} · scope {r.scope} · {JSON.stringify(r.config)}</div></div>
            </div>
            <StatusBadge tone={PREC_TONE[r.precedence]}>{r.precedence}</StatusBadge>
          </div>
        ))}
      </Card>

      <h2 className="mb-3 font-heading text-xl font-bold tracking-tight">Mängelkatalog (zentral)</h2>
      <Card className="divide-y divide-border rounded-md border">
        {defects.length === 0 && <div className="p-6 text-sm text-muted-foreground">Kein Katalog.</div>}
        {defects.map((d) => (
          <div key={d.code} data-testid="defect-row" className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3"><WarningDiamond size={16} className="text-warning" /><span className="font-mono text-xs text-primary">{d.code}</span><span className="text-sm">{d.text}</span></div>
            <StatusBadge tone={SEV_TONE[d.severity]}>{d.severity}</StatusBadge>
          </div>
        ))}
      </Card>
    </div>
  );
}
